import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MessageBubble } from './OptimizedMessageBubble';
import { DateSeparator } from './DateSeparator';
import { LoadOlderMessages } from './LoadOlderMessages';
import { IcebreakerSuggestions } from './IcebreakerSuggestions';
import { useScrollToTop } from '@/hooks/useScrollToTop';

import { Message } from '@/types/chat';
import { formatDateSeparator, isSameDay } from '@/utils/chatFormatters';
import { debugLogger } from '@/utils/debugLogger';

interface Partner {
  id: string;
  username?: string;
  avatar?: string;
  gender?: string;
  role?: string;
  relationship_type?: string;
  bio_topics?: string[];
}

interface UltraSimpleMessageListProps {
  messages: Message[];
  currentUserId: string;
  partner: Partner;
  hasOlderMessages?: boolean;
  loadingOlderMessages?: boolean;
  onReply?: (messageId: string, content: string, senderId: string) => void;
  onDelete?: (messageId: string) => void;
  onLoadOlderMessages?: () => void;
  onSelectIcebreaker?: (text: string) => void;
}

export const UltraSimpleMessageList: React.FC<UltraSimpleMessageListProps> = ({
  messages,
  currentUserId,
  partner,
  hasOlderMessages = false,
  loadingOlderMessages = false,
  onReply,
  onDelete,
  onLoadOlderMessages,
  onSelectIcebreaker
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const scrollToBottomTimeoutRef = useRef<NodeJS.Timeout>();
  const firstMessageIdRef = useRef<string | null>(null);

  // Hook para detectar scroll até o topo
  const scrollContainerRef = useScrollToTop({
    onScrollToTop: () => {
      debugLogger.debug('[UltraSimpleMessageList] onLoadOlderMessages triggered by scroll');
      onLoadOlderMessages?.();
    },
    threshold: 50,
    debounceMs: 1000,
    disabled: isInitializing
  });

  const scrollToBottom = useCallback(() => {
    if (scrollToBottomTimeoutRef.current) {
      clearTimeout(scrollToBottomTimeoutRef.current);
    }
    
    scrollToBottomTimeoutRef.current = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
      
      // Desabilita o modo de inicialização após primeiro scroll
      if (isInitializing) {
        setTimeout(() => {
          setIsInitializing(false);
          debugLogger.debug('[UltraSimpleMessageList] Initialization complete, scroll detection enabled');
        }, 2000);
      }
    }, 100);
  }, [isInitializing]);

  // Refined scroll logic - pure message comparison without state dependencies
  useEffect(() => {
    if (messages.length === 0) {
      debugLogger.debug(`[UltraSimpleMessageList] No messages, skipping scroll logic`);
      return;
    }

    const currentFirstMessageId = messages[0]?.id;
    const previousFirstMessageId = firstMessageIdRef.current;
    
    // Detailed logging for debugging
    debugLogger.debug(`[UltraSimpleMessageList] Scroll analysis:`, {
      messagesCount: messages.length,
      currentFirstMessageId,
      previousFirstMessageId,
      isInitializing,
      loadingOlderMessages
    });

    // Determine if older messages were loaded
    const olderMessagesWereLoaded = previousFirstMessageId && 
                                   previousFirstMessageId !== currentFirstMessageId;

    if (olderMessagesWereLoaded) {
      // Older messages loaded - preserve scroll position
      debugLogger.debug(`[UltraSimpleMessageList] ✅ OLDER messages detected - preserving scroll position`);
      // Do NOT scroll to bottom
    } else if (!previousFirstMessageId || isInitializing) {
      // Initial load
      debugLogger.debug(`[UltraSimpleMessageList] ✅ INITIAL load - scrolling to bottom`);
      scrollToBottom();
    } else {
      // New messages at the end
      debugLogger.debug(`[UltraSimpleMessageList] ✅ NEW messages - scrolling to bottom`);
      scrollToBottom();
    }

    // Always update the tracked first message ID
    firstMessageIdRef.current = currentFirstMessageId;
  }, [messages, scrollToBottom, isInitializing]);

  // Create a function to check if user is current user
  const isCurrentUser = (senderId: string) => senderId === currentUserId;

  // Create partner object with required properties
  const partnerForBubble = {
    id: partner.id,
    username: partner.username || 'Usuário',
    avatar: partner.avatar || ''
  };

  // Group messages and insert date separators
  const renderMessagesWithDateSeparators = () => {
    const elements: React.ReactNode[] = [];
    let lastMessageDate: Date | null = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.created_at);
      
      // Check if we need a date separator
      if (!lastMessageDate || !isSameDay(lastMessageDate, messageDate)) {
        const dateLabel = formatDateSeparator(message.created_at);
        if (dateLabel) {
          elements.push(
            <DateSeparator
              key={`date-${messageDate.toDateString()}`}
              date={dateLabel}
            />
          );
        }
      }

      // Add the message
      elements.push(
        <MessageBubble
          key={message.id}
          message={message}
          isCurrentUser={isCurrentUser}
          partner={partnerForBubble}
          onReply={onReply ? () => onReply(message.id, message.content || '', message.sender_id) : undefined}
          onDelete={onDelete ? () => onDelete(message.id) : undefined}
        />
      );

      lastMessageDate = messageDate;
    });

    return elements;
  };

  return (
    <div 
      ref={scrollContainerRef}
      className="flex-1 h-full overflow-y-scroll bg-black"
      style={{ 
        scrollbarGutter: 'stable'
      }}
    >
      <div className="w-full px-3 space-y-1 flex flex-col justify-start pb-4">
        <LoadOlderMessages
          hasOlderMessages={hasOlderMessages}
          isLoading={loadingOlderMessages}
          onLoadOlder={() => {
            debugLogger.debug('[UltraSimpleMessageList] onLoadOlderMessages triggered by button');
            onLoadOlderMessages?.();
          }}
        />
        {messages.length === 0 ? (
          <IcebreakerSuggestions
            partnerId={partner.id}
            partnerUsername={partner.username || 'esta pessoa'}
            onSelectSuggestion={onSelectIcebreaker || (() => {})}
          />
        ) : (
          renderMessagesWithDateSeparators()
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
